import { Form } from 'antd';
import React, {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import FilterActions from '../../../../../../RoomsAcceptance/shared/ui/FilterActions';
import FilterBtn from '../../../../../../RoomsAcceptance/shared/ui/FilterBtn';
import FilterContainer from '../../../../../../RoomsAcceptance/shared/ui/FilterContainer';
import Input from '../../../../../../RoomsAcceptance/shared/ui/Input';
import Item from '../../../../../../RoomsAcceptance/shared/ui/Item';
import BaseCheckbox from '../../../../../../RoomsAcceptance/shared/ui/BaseCheckbox';
import { ReactComponent as WorkerIcon } from '../../../../../../../resource/WorkerIcon.svg';
import styles from '../CreateNewWorkScheduleByPeriod.module.scss';
import { FormSection } from './FormSection';
import StyledSelect from './StyledSelect';
import { workerFilterFieldNames } from '../CreateNewWorkScheduleByPeriod.constants';

const WORKER_SEARCH_DEBOUNCE_MS = 250;

const useLatestRef = (value) => {
  const ref = useRef(value);

  useEffect(() => {
    ref.current = value;
  }, [value]);

  return ref;
};

const getOptionText = (option) =>
  [option.search, option.text, option.content, option.value]
    .filter(Boolean)
    .join(' ');

const createOptionSearch = (option) => getOptionText(option);

const getWorkerSpeciality = (option) => {
  if (option.data?.speciality) {
    return option.data.speciality;
  }

  if (typeof option.content !== 'string') {
    return '';
  }

  const match = option.content.match(/\((.*)\)$/);

  return match?.[1] || '';
};

const isMatchedSearch = (option, searchValue) => {
  const normalizedSearch = searchValue.trim().toLocaleLowerCase('ru-RU');

  if (!normalizedSearch) {
    return true;
  }

  return getOptionText(option)
    .toLocaleLowerCase('ru-RU')
    .includes(normalizedSearch);
};

const getSelectedFirstOptions = (options = [], selectedValues = []) => {
  const normalizedSelectedValues = Array.isArray(selectedValues)
    ? selectedValues
    : [selectedValues].filter(Boolean);
  const selectedValueSet = new Set(normalizedSelectedValues.map(String));

  if (!selectedValueSet.size) {
    return options;
  }

  return options
    .map((option, index) => ({ option, index }))
    .sort((first, second) => {
      const firstSelected = selectedValueSet.has(String(first.option.value));
      const secondSelected = selectedValueSet.has(String(second.option.value));

      if (firstSelected === secondSelected) {
        return first.index - second.index;
      }

      return firstSelected ? -1 : 1;
    })
    .map(({ option }) => option);
};

const getUniqueWorkerOptions = (options = []) => {
  const optionMap = new Map();

  options.forEach((option) => {
    const optionKey = String(option.value);

    if (!optionMap.has(optionKey)) {
      optionMap.set(optionKey, option);
    }
  });

  return [...optionMap.values()];
};

const WorkerItem = memo(({ option, isSelected, isEditable, onToggle }) => {
  const handleClick = useCallback(() => {
    onToggle(option.value);
  }, [onToggle, option.value]);
  const speciality = useMemo(() => getWorkerSpeciality(option), [option]);

  return (
    <Item.Memoized
      title={option.text || option.value}
      description={speciality}
      iconComponent={WorkerIcon}
      action={<BaseCheckbox checked={isSelected} />}
      isActive={isSelected}
      disabled={!isEditable}
      onClick={handleClick}
    />
  );
});

const WorkersFilter = ({
  form,
  isEditable,
  workerSpecialityOptions,
  workerRoleOptions,
  workersLoading,
  onClose,
}) => {
  const { getFieldDecorator, getFieldValue, setFieldsValue } = form;
  const specialityValue =
    getFieldValue(workerFilterFieldNames.speciality) || [];
  const roleValue = getFieldValue(workerFilterFieldNames.role) || [];
  const sortedWorkerSpecialityOptions = useMemo(
    () => getSelectedFirstOptions(workerSpecialityOptions, specialityValue),
    [specialityValue, workerSpecialityOptions]
  );
  const sortedWorkerRoleOptions = useMemo(
    () => getSelectedFirstOptions(workerRoleOptions, roleValue),
    [roleValue, workerRoleOptions]
  );
  const hasActiveFilters = Boolean(specialityValue.length || roleValue.length);

  const handleReset = () => {
    setFieldsValue({
      [workerFilterFieldNames.speciality]: [],
      [workerFilterFieldNames.role]: [],
    });
    onClose();
  };

  return (
    <FilterContainer>
      <Form.Item label="Специальность" className={styles.formItem}>
        {getFieldDecorator(workerFilterFieldNames.speciality, {
          initialValue: [],
        })(
          <StyledSelect
            createSearch={createOptionSearch}
            disabled={!isEditable || workersLoading}
            id="workerFilterSpeciality"
            loading={workersLoading}
            multiple
            name={workerFilterFieldNames.speciality}
            options={sortedWorkerSpecialityOptions}
            placeholder="Все специальности"
            showSelectionActions
          />
        )}
      </Form.Item>
      <Form.Item label="Роль" className={styles.formItem}>
        {getFieldDecorator(workerFilterFieldNames.role, {
          initialValue: [],
        })(
          <StyledSelect
            createSearch={createOptionSearch}
            disabled={!isEditable || workersLoading}
            id="workerFilterRole"
            loading={workersLoading}
            multiple
            name={workerFilterFieldNames.role}
            options={sortedWorkerRoleOptions}
            placeholder="Все роли"
            showSelectionActions
          />
        )}
      </Form.Item>
      <FilterActions
        resetDisabled={!hasActiveFilters}
        applyDisabled={!hasActiveFilters}
        onReset={handleReset}
        onApply={onClose}
      />
    </FilterContainer>
  );
};

export const WorkersSection = ({
  form,
  isEditable = true,
  initialSelectedWorkerIds = [],
  selectedWorkers,
  workerOptions,
  workerSpecialityOptions,
  workerRoleOptions,
  workersLoading,
  onAdd,
  onAddMany,
  onDelete,
  onDeleteMany,
  children,
}) => {
  const { getFieldValue } = form;
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
  const listRef = useRef(null);
  const pendingScrollRef = useRef(null);
  const rememberScroll = useCallback(() => {
    const positions = [];
    let element = listRef.current;

    while (element) {
      positions.push({
        element,
        top: element.scrollTop,
        left: element.scrollLeft,
      });
      element = element.parentElement;
    }

    pendingScrollRef.current = positions;
  }, []);
  const selectedWorkerIdSet = useMemo(
    () => new Set((selectedWorkers || []).map((worker) => String(worker.id))),
    [selectedWorkers]
  );
  const selectedWorkerIdSetRef = useLatestRef(selectedWorkerIdSet);
  const onAddRef = useLatestRef(onAdd);
  const onDeleteRef = useLatestRef(onDelete);
  const uniqueWorkerOptions = useMemo(
    () => getUniqueWorkerOptions(workerOptions),
    [workerOptions]
  );
  const filteredWorkerOptions = useMemo(
    () =>
      uniqueWorkerOptions.filter((option) =>
        isMatchedSearch(option, debouncedSearchValue)
      ),
    [debouncedSearchValue, uniqueWorkerOptions]
  );
  const sortedFilteredWorkerOptions = useMemo(
    () =>
      getSelectedFirstOptions(filteredWorkerOptions, initialSelectedWorkerIds),
    [filteredWorkerOptions, initialSelectedWorkerIds]
  );
  useLayoutEffect(() => {
    (pendingScrollRef.current || []).forEach(({ element, top, left }) => {
      element.scrollTop = top;
      element.scrollLeft = left;
    });
    pendingScrollRef.current = null;
  }, [sortedFilteredWorkerOptions, selectedWorkerIdSet]);

  const hasFilters = Boolean(
    (getFieldValue(workerFilterFieldNames.speciality) || []).length ||
      (getFieldValue(workerFilterFieldNames.role) || []).length
  );
  const visibleWorkerIds = useMemo(
    () => sortedFilteredWorkerOptions.map((option) => option.value),
    [sortedFilteredWorkerOptions]
  );
  const hasVisibleWorkers = Boolean(visibleWorkerIds.length);
  const hasVisibleUnselectedWorkers = useMemo(
    () =>
      visibleWorkerIds.some(
        (workerId) => !selectedWorkerIdSet.has(String(workerId))
      ),
    [selectedWorkerIdSet, visibleWorkerIds]
  );
  const hasVisibleSelectedWorkers = useMemo(
    () =>
      visibleWorkerIds.some((workerId) =>
        selectedWorkerIdSet.has(String(workerId))
      ),
    [selectedWorkerIdSet, visibleWorkerIds]
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
    }, WORKER_SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timeoutId);
  }, [searchValue]);

  const handleWorkerClick = useCallback(
    (workerId) => {
      if (!isEditable) {
        return;
      }

      rememberScroll();
      if (selectedWorkerIdSetRef.current.has(String(workerId))) {
        onDeleteRef.current(workerId);
        return;
      }

      onAddRef.current(workerId);
    },
    [isEditable, onAddRef, onDeleteRef, rememberScroll, selectedWorkerIdSetRef]
  );

  const handleSelectAll = useCallback(() => {
    if (!isEditable || !hasVisibleUnselectedWorkers) {
      return;
    }

    rememberScroll();
    onAddMany(visibleWorkerIds);
  }, [
    hasVisibleUnselectedWorkers,
    isEditable,
    onAddMany,
    rememberScroll,
    visibleWorkerIds,
  ]);

  const handleDeselectAll = useCallback(() => {
    if (!isEditable || !hasVisibleSelectedWorkers) {
      return;
    }

    rememberScroll();
    onDeleteMany(visibleWorkerIds);
  }, [
    hasVisibleSelectedWorkers,
    isEditable,
    onDeleteMany,
    rememberScroll,
    visibleWorkerIds,
  ]);

  return (
    <FormSection title="Исполнители">
      <div className={styles.workerToolbar}>
        <Input
          className={styles.workerSearch}
          disabled={workersLoading}
          id="workScheduleWorkerSearch"
          name="workerSearch"
          placeholder="Поиск исполнителя"
          type={Input.types.search}
          value={searchValue}
          onChange={(event) => setSearchValue(event.target.value)}
        />
        <FilterBtn
          isActive={hasFilters}
          renderContent={({ onClose }) => (
            <WorkersFilter
              form={form}
              isEditable={isEditable}
              workerSpecialityOptions={workerSpecialityOptions}
              workerRoleOptions={workerRoleOptions}
              workersLoading={workersLoading}
              onClose={onClose}
            />
          )}
        />
      </div>
      {hasVisibleWorkers && (
        <div className={styles.workerBulkActions}>
          <button
            type="button"
            className={styles.workerBulkButton}
            disabled={!isEditable || !hasVisibleUnselectedWorkers}
            onClick={handleSelectAll}
          >
            Выбрать всех
          </button>
          <button
            type="button"
            className={styles.workerBulkButton}
            disabled={!isEditable || !hasVisibleSelectedWorkers}
            onClick={handleDeselectAll}
          >
            Снять всех
          </button>
        </div>
      )}
      <ul className={styles.workerList} ref={listRef}>
        {sortedFilteredWorkerOptions.length ? (
          sortedFilteredWorkerOptions.map((option) => (
            <WorkerItem
              key={option.value}
              option={option}
              isSelected={selectedWorkerIdSet.has(String(option.value))}
              isEditable={isEditable}
              onToggle={handleWorkerClick}
            />
          ))
        ) : (
          <li className={styles.workerEmpty}>
            {workersLoading
              ? 'Загрузка исполнителей...'
              : 'Исполнители не найдены'}
          </li>
        )}
      </ul>
      {children}
    </FormSection>
  );
};
